import { useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Student modal to request cancellation of a placement (accepted/assigned app).
 * The request enters admin review; the seat is released only on admin approval.
 *
 * Props:
 *  - app: application object with id + opportunity summary.
 *  - onClose(): close without saving.
 *  - onSubmitted(): called after successful submission.
 */
export default function CancelPlacementModal({ app, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const opportunity = app.opportunity?.title || "الفرصة";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      toast.error("سبب الإلغاء مطلوب (10 أحرف على الأقل)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/cancel-request`, { reason: reason.trim() });
      toast.success("تم إرسال طلب الإلغاء إلى الإدارة");
      onSubmitted?.();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:px-4" data-testid="cancel-placement-modal">
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col animate-sheet-up sm:animate-fade-in-fast"
        style={{ maxHeight: "92dvh" }}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-rose-700">طلب إلغاء</div>
            <h2 className="mt-1 text-lg font-heading font-black text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
              إلغاء تسكينك في «{opportunity}»
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 min-h-11 min-w-11 grid place-items-center shrink-0"
            aria-label="إغلاق"
            data-testid="cancel-placement-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 leading-6">
            الطلب سيخضع لمراجعة الإدارة. لن يتم إلغاء تسكينك فعليًا حتى تعتمده الإدارة، وسيتم إشعارك بالقرار مع أي ملاحظات.
          </div>

          <label className="block">
            <span className="text-xs font-bold text-slate-600">
              سبب طلب الإلغاء <span className="text-rose-600">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              minLength={10}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="اشرح باختصار سبب رغبتك في الإلغاء (10 أحرف على الأقل)…"
              data-testid="cancel-placement-reason"
              required
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-xl px-4 text-sm font-bold text-slate-600 hover:bg-slate-100"
            data-testid="cancel-placement-cancel"
          >
            تراجع
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 min-h-11 rounded-xl bg-rose-600 px-5 text-sm font-bold text-white disabled:opacity-50 hover:bg-rose-700"
            data-testid="cancel-placement-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "جارٍ الإرسال…" : "إرسال طلب الإلغاء"}
          </button>
        </div>
      </form>
    </div>
  );
}
